/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  Flex,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiInfo } from 'react-icons/fi';
import { DataGrid } from '../../../components/datagrid';
import { SearchFilter } from '../../../components/inputs/filters/search';
import { PageTitle } from '../../../components/PageTitle';
import { useFetch } from '../../../hooks/useFetch';
import { getCellStatus } from '../../../utils/FormatFunctions';
import { useAuth } from '../../../providers/auth';
import { DataSkeleton, ErrorState } from '../../../components/states';

export function InstitutionsPage() {
  const baseApiUrl = '/v1/admin';
  const baseUrl = `${baseApiUrl}/institutions`;
  const { requestApi, data, loading, error } = useFetch(baseUrl);
  const { requestApi: reqApiDetails } = useFetch('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const groupIds = user?.group?.id;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedInstitution, setSelectedInstitution] = useState<any>(null);
  const [institutionDetails, setInstitutionDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const handleShowDetails = async (institution: any) => {
    setSelectedInstitution(institution);
    setLoadingDetails(true);
    onOpen();

    try {
      const response = await reqApiDetails(
        'get',
        {},
        `/v1/admin/institutions/${institution.id}/summary`
      );
      setInstitutionDetails(response?.data?.data || response?.data || null);
    } catch {
      setInstitutionDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const columns = [
    { name: 'id', label: 'Id' },
    { name: 'name', label: 'Nome' },
    { name: 'site', label: 'Site' },
    { name: 'responsible', label: 'Responsavel' },
    { name: 'status', label: 'Status' },
    { name: 'details', label: 'Detalhes' },
    { name: 'classes', label: 'Turmas' },
  ];

  return (
    <Box>
      <PageTitle>Instituições</PageTitle>

      <DataGrid
        columns={columns}
        rows={data?.data}
        requestApi={requestApi}
        error={error}
        baseUrl={baseUrl}
        data={data}
        hasCreate={(groupIds !== 7 && groupIds !== 3) ?? true}
        loading={loading}
        rowActions={{
          show: false,
          edit: (user?.group?.id !== 7 && user?.group?.id !== 3) ?? true,
          delete: groupIds !== 3,
        }}
        columnWithAction={[
          {
            name: 'details',
            columnAction: (rowItem) => (
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                leftIcon={<FiInfo />}
                onClick={() => handleShowDetails(rowItem)}
              >
                Ver detalhes
              </Button>
            ),
          },
          {
            name: 'classes',
            columnAction: (rowItem) => (
              <Button
                textTransform="uppercase"
                colorScheme="button.primary"
                onClick={() => navigate(`${rowItem.id}/teams`)}
              >
                Ver turmas
              </Button>
            ),
          },
          //   {
          //     name: 'status',
          //     columnAction: rowItem => (
          //       <SwitchInput
          //         values={rowItem}
          //         value={rowItem.status}
          //         changeStatus={(checked, values) =>
          //           onToggleSwitch({
          //             requestToggle,
          //             method: 'patch',
          //             checked,
          //             name: 'user',
          //             path: `${baseApiUrl}/globals/status/${values.id}`,
          //             variable: 'status',
          //           })
          //         }
          //       />
          //     ),
          //   },
        ]}
        formatColum={[
          {
            field: 'status',
            formatField: getCellStatus,
          },
        ]}
        headerFilters={[
          {
            name: 'search',
            filterComponent: SearchFilter,
          },
        ]}
      />

      {/* Modal de Detalhes */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Detalhes da Instituicao
            {selectedInstitution && (
              <Text fontSize="md" fontWeight="normal" color="gray.600" mt={1}>
                {selectedInstitution.name}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingDetails && (
              <DataSkeleton variant="detail" rows={8} label="Carregando detalhes da instituição" />
            )}
            {!loadingDetails && institutionDetails && (
              <Box>
                <Flex direction="column" gap={4}>
                  {/* Template */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Template
                    </Text>
                    <Badge
                      colorScheme={
                        institutionDetails.template_strategy === 'DEFAULT' ? 'green' : 'blue'
                      }
                    >
                      {institutionDetails.template_strategy === 'DEFAULT'
                        ? 'Padrao'
                        : 'Personalizado'}
                    </Badge>
                  </Box>

                  {/* Materiais Extras */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Materiais Extras
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                      {institutionDetails.extra_materials_count || 0}
                    </Text>
                  </Box>

                  {/* Templates de Materiais */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Templates de Materiais
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                      {institutionDetails.material_templates_count || 0}
                    </Text>
                  </Box>

                  {/* Categorias */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Categorias
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                      {institutionDetails.categories_count || 0}
                    </Text>
                  </Box>

                  {/* Modulos */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Modulos (School Classes)
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="teal.600">
                      {institutionDetails.school_classes_count || 0}
                    </Text>
                  </Box>

                  {/* Alunos */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Alunos
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="pink.600">
                      {institutionDetails.students_count || 0}
                    </Text>
                  </Box>

                  {/* Professores */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Professores
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="cyan.600">
                      {institutionDetails.teachers_count || 0}
                    </Text>
                  </Box>

                  {/* Tutores */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Tutores
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {institutionDetails.tutors_count || 0}
                    </Text>
                  </Box>

                  {/* Avaliações Registradas */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Avaliações registradas
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="pink.600">
                      {institutionDetails.child_ratings_count || 0}
                    </Text>
                  </Box>

                  {/* Média por aluno */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Média de avaliações por aluno
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="teal.500">
                      {typeof institutionDetails.ratings_per_student_avg === 'number'
                        ? institutionDetails.ratings_per_student_avg.toFixed(2)
                        : '0.00'}
                    </Text>
                  </Box>

                  {/* Turmas */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Turmas
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                      {institutionDetails.teams_count || 0}
                    </Text>
                  </Box>

                  {/* Encontros/Conteudos */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Encontros
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                      {institutionDetails.contents_count || 0}
                    </Text>
                  </Box>

                  {/* Agendamentos */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Agendamentos
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                      {institutionDetails.content_schedules_count || 0}
                    </Text>
                  </Box>

                  {/* Semanas */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Semanas
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="teal.600">
                      {institutionDetails.weeks_count || 0}
                    </Text>
                  </Box>

                  {/* Media Semanas por Categoria */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                      Media Semanas/Categoria
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="cyan.600">
                      {institutionDetails.weeks_per_category_avg?.toFixed(2) || '0.00'}
                    </Text>
                  </Box>

                  {/* REQ-021: Dados de Login - APENAS para SECRETARIA */}
                  {groupIds === 6 && (
                    <Box
                      mt={4}
                      p={4}
                      bg="blue.50"
                      borderRadius="md"
                      borderLeft="4px solid"
                      borderColor="blue.500"
                    >
                      <Text fontSize="sm" fontWeight="bold" color="blue.700" mb={2}>
                        Dados de Login da Instituição
                      </Text>
                      {institutionDetails.responsible_login ? (
                        <Box>
                          <Text fontSize="sm" color="gray.700">
                            <strong>Responsável:</strong>{' '}
                            {institutionDetails.responsible_login.name}
                          </Text>
                          <Text fontSize="sm" color="gray.700">
                            <strong>Email de Login:</strong>{' '}
                            {institutionDetails.responsible_login.email}
                          </Text>
                        </Box>
                      ) : (
                        <Text fontSize="sm" color="gray.500" fontStyle="italic">
                          Nenhum responsável cadastrado para esta instituição
                        </Text>
                      )}
                    </Box>
                  )}
                </Flex>
              </Box>
            )}
            {!loadingDetails && !institutionDetails && (
              <ErrorState
                title="Detalhes indisponíveis"
                description="Não foi possível carregar os detalhes da instituição."
                onRetry={() => selectedInstitution && handleShowDetails(selectedInstitution)}
              />
            )}
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
